/**
 * `venue.menu-hours` — data schemas. The first reference module (Arka
 * evidence): typed menus/categories/items and typed opening hours.
 * Weekday semantics are TYPED, never inferred from localized strings.
 */
import { z } from 'zod';
import { Slug } from '../../schema/types.js';

export const MenuItem = z.object({
  id: Slug,
  name: z.string().min(1).max(160),
  description: z.string().max(400).optional(),
  /** Display price exactly as the owner writes it ("189 Kč", "0,4l 55 Kč"). */
  price: z.string().max(40),
});

export const MenuCategory = z.object({
  id: Slug,
  name: z.string().min(1).max(120),
  items: z.array(MenuItem),
});

export const Weekday = z.enum(['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun']);

const time = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'HH:MM');

export const HoursRule = z.union([
  z.object({ days: z.array(Weekday).min(1), open: time, close: time }),
  z.object({ days: z.array(Weekday).min(1), closed: z.literal(true) }),
]);

export const HoursException = z.union([
  z.object({ date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/), open: time, close: time, note: z.string().max(120).optional() }),
  z.object({ date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/), closed: z.literal(true), note: z.string().max(120).optional() }),
]);

export const VenueMenuHoursData = z.object({
  menus: z.array(MenuCategory),
  hours: z.object({
    regular: z.array(HoursRule),
    exceptions: z.array(HoursException),
  }),
});

export const VenueMenuHoursConfig = z.object({
  /** Locale for weekday labels in rendered views. Defaults to Czech. */
  locale: z.enum(['cs', 'en', 'pl']).default('cs'),
});

export type MenuItemT = z.infer<typeof MenuItem>;
export type MenuCategoryT = z.infer<typeof MenuCategory>;
export type WeekdayT = z.infer<typeof Weekday>;
export type VenueMenuHoursDataT = z.infer<typeof VenueMenuHoursData>;
export type VenueMenuHoursConfigT = z.infer<typeof VenueMenuHoursConfig>;
